import type { NodeOptions } from '@sentry/nextjs';

/**
 * Span policy for the server tracer `@sentry/nextjs` registers, and the query
 * redaction every Sentry URL goes through. Pure, so the browser bundle can
 * import it.
 */

/**
 * Spans the server SDK drops before a transaction leaves the process, passed
 * as `ignoreSpans`. A match on the root span drops the whole transaction; a
 * match on a child moves that span's own children up to its parent.
 */
export const IGNORED_SPANS: NonNullable<NodeOptions['ignoreSpans']> = [
  // Kubernetes probes this every few seconds per pod.
  { attributes: { 'http.target': /^\/api\/upcheck(?:[?#]|$)/ } },
  // Prisma wraps each operation in a serialize span that repeats its parent's
  // timing to within a fraction of a millisecond.
  /^prisma:client:serialize$/,
  /^prisma:engine:serialize$/,
  /^prisma:engine:response_json_serialization$/,
  // Next.js module and component resolution. The route handler span that
  // wraps them already carries the time they measure.
  /^resolve page components$/,
  // @sentry/nextjs renames this span on start ("resolve layout server
  // component …"), before ignoreSpans runs, so only Next's own attribute
  // still identifies it.
  { attributes: { 'next.span_name': /^resolve segment modules$/ } },
];

const REDACTED = '[redacted]';

/**
 * Replaces every query-string value in a string that ends in a request target,
 * keeping the path and the parameter names. Callers pass a bare target, an
 * absolute URL, or a span name that ends in one.
 *
 * A recruiter's search term travels in all of them: `?query=carmen` is an
 * applicant's name. Redacting every value rather than a named list means a new
 * parameter is private the day someone adds it.
 *
 * The path keeps its IDs. An opening or applicant ID is what makes a failing
 * request findable again, and it names a record rather than a person. The
 * parameter names stay for the same reason: they separate a search from a
 * plain list read without saying what was searched for.
 *
 * `keep` lets a caller spare a value it can prove is not free text.
 */
export function redactQueryValues(
  target: string,
  keep: (name: string, value: string) => boolean = () => false
): string {
  const split = target.indexOf('?');
  if (split === -1) {
    return target;
  }

  const query = target
    .slice(split + 1)
    .split('&')
    .map((pair) => {
      const equals = pair.indexOf('=');
      // A parameter with no value has nothing to hide.
      if (equals === -1) {
        return pair;
      }
      const name = pair.slice(0, equals);
      return keep(name, pair.slice(equals + 1)) ? pair : `${name}=${REDACTED}`;
    })
    .join('&');

  return `${target.slice(0, split)}?${query}`;
}
